import React from 'react';
import { formatDateForGroup } from '../../../utils/date';
import type { DerivedWorkOrder } from '../hooks/useWorkOrdersPage';
import type { InventoryItem } from '../../../types/inventory';
import { useLanguage } from '../../../features/language/useLanguage';

interface WorkOrdersCardsProps {
  orders: DerivedWorkOrder[];
  shouldGroup: boolean;
  groupedOrders: Record<string, DerivedWorkOrder[]>;
  sortedDates: string[];
  roastTypeLabelMap: Record<string, string>;
  formatDate: (value?: string) => string;
  formatNumber: (value: number | null | undefined, digits?: number) => string | null;
  formatPercent: (value: number | null | undefined) => string | null;
  onEditOrder: (id: string) => void;
  onCompleteOrder: (id: string) => void;
  onCancelOrder: (order: DerivedWorkOrder) => void;
  onDeleteOrder: (order: DerivedWorkOrder) => void;
  onMoveToInventory: (order: DerivedWorkOrder) => void;
  inventoryItems: InventoryItem[];
  canWrite?: boolean;
  canDelete?: boolean;
}

export const WorkOrdersCards: React.FC<WorkOrdersCardsProps> = ({
  orders,
  shouldGroup,
  groupedOrders,
  sortedDates,
  roastTypeLabelMap,
  formatDate,
  formatNumber,
  formatPercent,
  onEditOrder,
  onCompleteOrder,
  onCancelOrder,
  onDeleteOrder,
  onMoveToInventory,
  inventoryItems,
  canWrite = true,
  canDelete = true,
}) => {
  const { t } = useLanguage();
  const statusLabel = (status: string) =>
    t(`workOrders.status.${status}`, undefined, status.replace('_', ' '));

  const renderCard = (order: DerivedWorkOrder) => {
    const shouldShowShrinkage =
      formatNumber(order.shrinkageKg) !== null &&
      formatNumber(order.shrinkagePercent) !== null &&
      !(order.status !== 'completed' && (order.outputWeight ?? 0) === 0);

    const shrinkageText = shouldShowShrinkage
      ? `${formatNumber(order.shrinkageKg)} kg (${formatPercent(order.shrinkagePercent)}%)`
      : '-';

    const hasInventoryForWO = inventoryItems.some(item => item.workOrderId === order.id);

    return (
      <div
        key={order.id}
        className="p-4 space-y-3 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => {
          onEditOrder(order.id);
        }}
      >
        <div className="flex justify-between items-start gap-2">
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-900" data-test-id={`wo-number-mobile-${order.id}`}>{order.workOrderNumber || '—'}</p>
          </div>
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              order.status === 'completed'
                ? 'bg-green-100 text-green-800'
                : order.status === 'in_progress'
                  ? 'bg-blue-100 text-blue-800'
                  : order.status === 'cancelled'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {statusLabel(order.status)}
          </span>
        </div>

        {order.internalNotes && (
          <p className="text-sm text-gray-500 italic bg-gray-50 p-2 rounded break-words whitespace-pre-wrap">{order.internalNotes}</p>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
          <div>
            <div className="font-semibold">{t('workOrders.table.item')}</div>
            <div className="text-gray-900 truncate">{order.resolvedItemName || '-'}</div>
            <div className="text-gray-500 text-[11px]">{order.resolvedItemCode || '-'}</div>
          </div>
          <div>
            <div className="font-semibold">{t('workOrders.table.roastType')}</div>
            <div>{order.roastType ? roastTypeLabelMap[order.roastType] ?? order.roastType : '-'}</div>
            {(() => {
              const agtron = order.roastProfile?.agtron;
              if (
                !agtron ||
                typeof agtron.wholeBean !== 'number' ||
                typeof agtron.groundBean !== 'number' ||
                (agtron.wholeBean === 0 && agtron.groundBean === 0)
              ) {
                return null;
              }
              const diff = Number((agtron.wholeBean - agtron.groundBean).toFixed(2));
              return (
                <div
                  className="text-[11px] text-primary-700 font-semibold tabular-nums"
                  data-test-id="roast-type-agtron"
                  title={`Whole: ${agtron.wholeBean} / Ground: ${agtron.groundBean}`}
                >
                  {agtron.wholeBean}/{agtron.groundBean}{' '}
                  <span className="text-primary-600">({diff})</span>
                </div>
              );
            })()}
          </div>
          <div>
            <div className="font-semibold">{t('workOrders.cards.input')}</div>
            <div>{formatNumber(order.inputWeight) ?? '-'} kg</div>
          </div>
          <div>
            <div className="font-semibold">{t('workOrders.cards.output')}</div>
            <div>{formatNumber(order.outputWeight) ?? '-'} kg</div>
          </div>
          <div>
            <div className="font-semibold">{t('workOrders.table.shrinkage')}</div>
            <div>{shrinkageText}</div>
          </div>
          <div>
            <div className="font-semibold">{t('workOrders.cards.order')}</div>
            <div>{formatDate(order.date)}</div>
          </div>
          <div>
            <div className="font-semibold">{t('workOrders.table.actualEnd')}</div>
            <div>{formatDate(order.endDate)}</div>
          </div>
        </div>

        <div className="flex justify-between items-center text-sm text-gray-500 pt-2">
          <div className="space-x-2">
            {order.status === 'draft' && canWrite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditOrder(order.id);
                }}
                className="text-blue-600 hover:text-blue-900"
                data-test-id={`wo-start-button-${order.id}`}
              >
                {t('workOrders.actions.start')}
              </button>
            )}
            {order.status === 'scheduled' && canWrite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCompleteOrder(order.id);
                }}
                className="text-green-600 hover:text-green-900"
                data-test-id={`wo-complete-button-${order.id}`}
              >
                {t('workOrders.actions.complete')}
              </button>
            )}
            {(order.status === 'draft' || order.status === 'scheduled') && canWrite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelOrder(order);
                }}
                className="text-red-600 hover:text-red-900"
                data-test-id={`wo-cancel-button-${order.id}`}
              >
                {t('workOrders.actions.cancel')}
              </button>
            )}
            {order.status === 'cancelled' && canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteOrder(order);
                }}
                className="text-red-700 hover:text-red-900"
                data-test-id={`wo-delete-button-${order.id}`}
              >
                {t('workOrders.actions.delete')}
              </button>
            )}
            {order.status === 'completed' && (
              <button
                onClick={(e) => {
                  if (canWrite) {
                    e.stopPropagation();
                    onMoveToInventory(order);
                  }
                }}
                className={`font-medium ${
                  canWrite 
                    ? 'text-purple-600 hover:text-purple-900 cursor-pointer' 
                    : 'text-gray-400 cursor-default'
                }`}
                data-test-id={`wo-move-inv-button-${order.id}`}
              >
                {hasInventoryForWO ? t('workOrders.actions.updateStock') : t('workOrders.actions.stockIn')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="md:hidden divide-y divide-gray-200">
      {shouldGroup ? (
        sortedDates.map((dateKey) => (
          <React.Fragment key={dateKey}>
            <div className="bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">
              {formatDateForGroup(dateKey)}
            </div>
            {groupedOrders[dateKey].map(renderCard)}
          </React.Fragment>
        ))
      ) : (
        orders.map(renderCard)
      )}
    </div>
  );
};
